import {Inject, inject, Injectable, NgZone} from '@angular/core';
import {datadogRum} from '@datadog/browser-rum';
import {BehaviorSubject, fromEvent, interval, Observable, of, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, take} from 'rxjs/operators';
import {DATADOG_RUM_CONFIG, PortalDatadogIdentifier} from '../constants/datadog-init.constants';
import {createCommonMetadata} from '../helpers/local-storage/common-metatada.helper';
import {areCookiesAllowed, getAccessToken} from '../helpers/local-storage/local-storage.helper';
import {IDataDogConfig, IInternalDataDogConfig, ILogContext} from '../interfaces/config.interface';
import {IDatadogRumConfig} from '../interfaces/datadog-config.interface';
import {ConfigService} from './http-services/config.service';

@Injectable()
export class DatadogRumLoggerService {
  public zone = inject(NgZone);
  public areCookiesAllowed = areCookiesAllowed();
  public readonly cookiesAccepted$ = new BehaviorSubject(this.areCookiesAllowed);
  private readonly subscription = new Subscription();


  constructor(
    @Inject(DATADOG_RUM_CONFIG) protected datadogConfig: IDatadogRumConfig,
    protected configService: ConfigService
  ) {}

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // Method should be called on app root onInit - can't be called inside service constructor
  // https://github.com/angular/angular/issues/25590
  public load(): void {
    const obs$ = this.getDatadogConfig$();

    this.subscription.add(
      obs$.subscribe(config => {
        if (config.enabled && config.setup) {
          this.init(config.setup);
          // Log CSP violations into DD-RUM, while CSP header is in report mode
          this.securityPolicyViolation();
        }
      })
    );
  }

  // Log Custom Events
  public logAction(eventName: string, context?: ILogContext, appName: string = this.datadogConfig.service): void {
    const event = `${appName} | ${eventName}`;

    const metadata = {
      common: createCommonMetadata(),
      app: context
    };

    datadogRum.addAction(event, metadata);
  }

  public logError(eventName: string, context?: ILogContext, appName: string = this.datadogConfig.service): void {
    const message = `${appName} | ${eventName}`;

    const metadata = {
      common: createCommonMetadata(),
      app: context
    };

    datadogRum.addError(message, metadata);
  }

  private init(config: IDataDogConfig): void {
    const {service, appVersion, tracesLinkEnabled} = this.datadogConfig;

    Object.assign(config, {
      service: service ?? 'default-service',
      version: appVersion ?? 'not-specified',
      trackUserInteractions: false
    });

    if (tracesLinkEnabled) {
      Object.assign(config, {
        //  ^https:\/\/portal → Ensures the URL starts with https://portal.
        //  (?:\.[a-z0-9-]+)? → Allows an optional subdomain before the main domain.
        //  \.[a-z0-9-]+ → Allows any main domain name (e.g., jumio, jumiopp, xyz, etc.).
        //  \.[a-z]+ → Allows any top-level domain (.link, .ai, .com, .net, .xyz, etc.).
        //  \/api.*$ → Ensures the URL contains /api and anything that follows.
        allowedTracingUrls: [/^https:\/\/portal(?:\.[a-z0-9-]+)?\.[a-z0-9-]+\.[a-z]+\/api.*$/],
        trackResources: true
      });
    }

    datadogRum.init(config);
  }

  private getDatadogConfig$(): Observable<Partial<IInternalDataDogConfig>> {
    const checkTokenExists$ = interval(1000).pipe(
      map(() => !!getAccessToken()),
      filter(tokenExists => !!tokenExists),
      take(1)
    );

    const fetchDatadogConfig$ = this.configService.datadogConfig$(
      this.datadogConfig.portalIdentifier ?? PortalDatadogIdentifier.UNIFIED_PORTAL
    );

    const checkCookiesAccepted$ = this.cookiesAccepted$.pipe(
      distinctUntilChanged((prev, cur) => prev === cur), // Only if the cookiesConsent value changed
      switchMap(cookiesAccepted => (cookiesAccepted ? fetchDatadogConfig$ : of({enabled: false})))
    );

    // If this.config.ignoreCookies === true - doesn't need to check cookies or token
    if (!!this.datadogConfig.ignoreCookies) {
      return fetchDatadogConfig$;
    }

    /**
     * If this.config.ignoreCookies === false - need to check cookies
     */

    // Check if it needs to wait for token
    if (!!this.datadogConfig.waitForToken) {
      // If it needs to wait for the token, then it should only fetch DD config, after token is available
      return checkTokenExists$.pipe(switchMap(tokenExists => (tokenExists ? checkCookiesAccepted$ : of({enabled: false}))));
    }

    // Otherwise, simply fetch DD config in case cookies are accepted
    return checkCookiesAccepted$;
  }

  // Log CSP violations into DD-RUM, while CSP header is in report mode
  private securityPolicyViolation(): void {
    this.zone.runOutsideAngular(() => {
      this.subscription.add(
        fromEvent<SecurityPolicyViolationEvent>(window, 'securitypolicyviolation').subscribe((event: SecurityPolicyViolationEvent) => {
          const cspReport = {
            blockedURI: event.blockedURI,
            documentURI: event.documentURI,
            violatedDirective: event.violatedDirective,
            originalPolicy: event.originalPolicy,
            sourceFile: event.sourceFile,
            lineNumber: event.lineNumber.toString(),
            columnNumber: event.columnNumber.toString(),
            statusCode: event.statusCode.toString()
          };

          this.logAction('CSP-header-violation', {meta: cspReport});
        })
      );
    });
  }
}
